import React from 'react';
import styled from 'styled-components';
import { media } from '@peloton/styles';
import { track } from '@engage/analytics';
import { slate2 } from '@engage/colors';
import { getFilterAnalyticsProps } from '@engage/filters';
import {
  trackViewedEmptyLibraryPage,
  EmptyLibrarySource,
  EmptyStateType,
} from '@engage/library';
import { useReduxAction, useReduxState } from '@engage/redux';
import {
  loadDetails,
  trackViewedClassLanguageModal,
  Source,
  getClassLanguagePreferences,
  getBlockExplicit,
} from '@engage/settings';
import { spaces } from '@engage/styles';
import { header2Small, header1Small } from '@engage/typography';
import { Button3Large } from '@members/buttons';
import { SettingsCopy, Settings, ClassesCopy, Classes, useSettings } from '@members/copy';
import { Modal } from '@members/modal';
import { modalProps, ClassLanguageModal } from './ClassLanguageModal';

type Props = {
  onSave: () => void;
  source: Source;
};

const useOpenClose = (source: Source) => {
  const trackAction = useReduxAction(track);
  const [isOpen, setIsOpen] = React.useState(false);
  const close = () => setIsOpen(false);
  const open = () => {
    trackAction(trackViewedClassLanguageModal(source));
    setIsOpen(true);
  };
  return { isOpen, close, open };
};

export const NoClassesBecauseLanguage: React.FC<React.PropsWithChildren<Props>> = ({
  onSave,
  source,
}) => {
  const { isOpen, close, open } = useOpenClose(source);
  const fetchSettings = useReduxAction(loadDetails);
  const trackAction = useReduxAction(track);
  const classLanguageSettings = useReduxState(getClassLanguagePreferences);
  const browseProps = useReduxState(getFilterAnalyticsProps);
  const explicitBlocked = useReduxState(getBlockExplicit);

  React.useEffect(() => {
    fetchSettings();

    trackAction(
      trackViewedEmptyLibraryPage(
        source === Source.ScheduleLimitedResults
          ? EmptyLibrarySource.Schedule
          : EmptyLibrarySource.Library,
        EmptyStateType.LanguageSettings,
        classLanguageSettings,
        explicitBlocked,
        browseProps,
      ),
    );
  }, []);

  return (
    <>
      <Container>
        <Content>
          <Header data-test-id="noClassesMessage">
            <ClassesCopy id={Classes.NoClassesFound} />
          </Header>
          <InfoText>
            <SettingsCopy id={Settings.NoClassesBecauseLanguagesMessage} />
          </InfoText>
          <Button3Large data-test-id="classLanguageCTA" onClick={open}>
            <SettingsCopy id={Settings.ClassLanguageCTA} />
          </Button3Large>
        </Content>
      </Container>
      <Modal
        isOpen={isOpen}
        closeHandler={close}
        contentLabel={useSettings(Settings.LanguagesModal)}
        {...modalProps}
      >
        <ClassLanguageModal
          closeModal={close}
          onSuccessfulSave={() => {
            close();
            onSave();
          }}
          source={source}
        />
      </Modal>
    </>
  );
};

const Container = styled.div`
  flex: 1;
  width: 100%;
  display: grid;
  grid-template-areas: 'content .';
  grid-template-rows: repeat(auto-fill, 50%);
  grid-template-columns: 1fr;
`;

const Content = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-direction: column;
  grid-area: content;
  color: ${slate2};
  text-align: center;
`;

const Header = styled.h1`
  margin-bottom: ${spaces.xSmall}px;
  ${header2Small}

  ${media.desktop`
    margin-bottom: ${spaces.medium}px;
    ${header1Small}
  `}
`;

const InfoText = styled.p`
  margin: 0px auto 25px;
`;
